import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  CheckQuestion,
  isCheckQuestion,
  isLongQuestion,
  isRadioQuestion,
  isScaleQuestion,
  LongQuestion,
  RadioQuestion,
  ScaleQuestion,
  TypedQuestion
} from 'types/question-response.types';
import { RadioField, RadioFieldItem } from '@epoch-ui/components/Form/fields/RadioField/RadioField';
import { ButtonGroupField } from '@epoch-ui/components/Form';
import { ButtonGroupItem } from '@epoch-ui/components/ButtonGroup';
import { InputField } from '@epoch-ui/components/Form/fields/InputField/InputField';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import { cn } from '@epoch-ui/lib/utils';

import { CheckboxGroupField } from './CheckboxGroupField';
import { OTHER_OPTION } from './schema/base.types';

export interface QuestionnaireFieldsProps {
  questions: TypedQuestion[];
  fieldNamePrefix: string;
  disabled?: boolean;
}

interface BaseQuestionFieldProps {
  question: TypedQuestion;
  fieldName: string;
  disabled: boolean;
}

type QuestionFieldProps<T extends TypedQuestion> = BaseQuestionFieldProps & { question: T };

const SingleChoiceQuestionField: React.FC<QuestionFieldProps<RadioQuestion>> = ({
  question,
  fieldName,
  disabled
}): JSX.Element => {
  const { control } = useFormContext();
  const valueFieldName = `${fieldName}.selectedOption`;
  const fieldValue = useWatch({ control, name: valueFieldName });

  const isOtherSelected = question.hasOtherOption && fieldValue === OTHER_OPTION;

  return (
    <RadioField
      key={question.id}
      name={valueFieldName}
      label={
        <div className="tw-mb-4">
          {question.label}
          {question.isRequired && <span className="tw-text-primary-blue"> *</span>}
        </div>
      }
      disabled={disabled}
    >
      {question.options?.map((option) => (
        <RadioFieldItem key={option} value={option}>
          <span>{option}</span>
        </RadioFieldItem>
      ))}
      {question.hasOtherOption && (
        <div className="tw-flex tw-items-center tw-gap-3">
          <RadioFieldItem value={OTHER_OPTION}>
            <span>{isOtherSelected ? 'Other:' : 'Other'}</span>
          </RadioFieldItem>
          {isOtherSelected && (
            <InputField
              name={`${fieldName}.otherValue`}
              disabled={disabled}
              className="tw-mt-0"
              inputClassName="tw-mt-0 tw-h-auto tw-rounded-none tw-w-52 tw-border-0 tw-border-b tw-border-solid tw-border-border-grey tw-bg-transparent tw-p-0 tw-outline-none"
            />
          )}
        </div>
      )}
    </RadioField>
  );
};

const MultipleChoiceQuestionField: React.FC<QuestionFieldProps<CheckQuestion>> = ({
  question,
  fieldName,
  disabled
}): JSX.Element => {
  const { control, setValue } = useFormContext();
  const valueFieldName = `${fieldName}.selectedOptions`;
  const fieldValue = useWatch({ control, name: valueFieldName });

  const currentValue = (fieldValue as string[] | undefined) || [];
  const isOtherChecked =
    question.hasOtherOption && Array.isArray(fieldValue) && currentValue.includes(OTHER_OPTION);

  const toggleOther = (isChecked: boolean): void => {
    const newValues = isChecked
      ? [...currentValue, OTHER_OPTION]
      : currentValue.filter((v) => v !== OTHER_OPTION);
    setValue(valueFieldName, newValues);
  };

  return (
    <CheckboxGroupField
      key={question.id}
      name={valueFieldName}
      label={
        <>
          {question.label}
          {question.isRequired && <span className="tw-text-primary-blue"> *</span>}
        </>
      }
      options={question.options || []}
      disabled={disabled}
    >
      {question.hasOtherOption && (
        <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">
          <label
            className={cn(
              'tw-flex tw-items-center tw-gap-3',
              disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
            )}
          >
            <Checkbox
              checked={isOtherChecked}
              disabled={disabled}
              onCheckedChange={(checked) => toggleOther(!!checked)}
            />
            <span className={cn('tw-text-sm tw-text-black', disabled && 'tw-opacity-40')}>
              {isOtherChecked ? 'Other:' : 'Other'}
            </span>
          </label>
          {isOtherChecked && (
            <InputField
              name={`${fieldName}.otherValue`}
              disabled={disabled}
              className="tw-mt-0"
              inputClassName="tw-mt-0 tw-h-auto tw-rounded-none tw-w-52 tw-border-0 tw-border-b tw-border-solid tw-border-border-grey tw-bg-transparent tw-p-0 tw-outline-none"
            />
          )}
        </div>
      )}
    </CheckboxGroupField>
  );
};

const LinearScaleQuestionField: React.FC<QuestionFieldProps<ScaleQuestion>> = ({
  question,
  fieldName,
  disabled
}): JSX.Element => {
  const { control, setValue } = useFormContext();
  const valueFieldName = `${fieldName}.value`;
  const fieldValue = useWatch({ control, name: fieldName });
  const scaleMinValue = question.properties?.scaleMinValue ?? 1;

  const scaleMaxValue = question.properties?.scaleMaxValue ?? 5;
  const range = Array.from(
    { length: scaleMaxValue - scaleMinValue + 1 },
    (_, index) => index + scaleMinValue
  );
  return (
    <ButtonGroupField
      key={question.id}
      name={valueFieldName}
      disabled={disabled}
      className="tw-inline-flex tw-flex-col tw-gap-3"
      label={
        <div className="tw-mb-4">
          {question.label}
          {question.isRequired && <span className="tw-text-primary-blue"> *</span>}
        </div>
      }
    >
      <div className="tw-inline-flex tw-max-w-full tw-flex-row tw-gap-2">
        {range.map((value) => (
          <ButtonGroupItem
            key={value}
            value={String(value)}
            className={cn(
              'tw-aspect-square tw-h-12 tw-w-12 tw-shrink tw-rounded-rounded tw-p-0',
              'tw-flex tw-items-center tw-justify-center tw-border-[1.5px] tw-border-solid',
              fieldValue === String(value)
                ? 'tw-border-primary-blue tw-bg-light-blue-badge tw-text-primary-blue'
                : 'tw-border-border-grey tw-bg-background-grey tw-text-primary-grey',
              !disabled &&
                fieldValue !== String(value) &&
                'hover:tw-border-primary-blue hover:tw-bg-background-grey',
              disabled && 'tw-pointer-events-none tw-opacity-40'
            )}
            onClick={(e) => {
              if (fieldValue === String(value)) {
                e.preventDefault();
                e.stopPropagation();
                setValue(fieldName, undefined);
              }
            }}
          >
            {String(value)}
          </ButtonGroupItem>
        ))}
      </div>
      {(question.properties?.scaleMinLabel || question.properties?.scaleMaxLabel) && (
        <div className="tw-flex tw-w-full tw-flex-row tw-justify-between tw-gap-2 tw-text-sm tw-text-primary-grey">
          <span className="tw-text-left">{question.properties?.scaleMinLabel}</span>
          <span className="tw-text-right">{question.properties?.scaleMaxLabel}</span>
        </div>
      )}
    </ButtonGroupField>
  );
};

const TextQuestionField: React.FC<QuestionFieldProps<LongQuestion>> = ({
  question,
  fieldName,
  disabled
}): JSX.Element => {
  const valueFieldName = `${fieldName}.value`;
  return (
    <InputField
      key={question.id}
      name={valueFieldName}
      label={
        <div className="tw-mb-4">
          {question.label}
          {question.isRequired && <span className="tw-text-primary-blue"> *</span>}
        </div>
      }
      placeholder="Your answer"
      disabled={disabled}
      className="tw-mt-0"
    />
  );
};

export const QuestionnaireFields: React.FC<QuestionnaireFieldsProps> = ({
  questions,
  fieldNamePrefix,
  disabled = false
}): JSX.Element => {
  return (
    <>
      {questions.map((question) => {
        if (!question.id) return null;
        const props = {
          fieldName: `${fieldNamePrefix}.${question.id}`,
          disabled
        };
        if (isRadioQuestion(question)) {
          return <SingleChoiceQuestionField key={question.id} {...{ question, ...props }} />;
        } else if (isCheckQuestion(question)) {
          return <MultipleChoiceQuestionField key={question.id} {...{ question, ...props }} />;
        } else if (isLongQuestion(question)) {
          return <TextQuestionField key={question.id} {...{ question, ...props }} />;
        } else if (isScaleQuestion(question)) {
          return <LinearScaleQuestionField key={question.id} {...{ question, ...props }} />;
        } else {
          return null;
        }
      })}
    </>
  );
};
